import { useEffect, useRef, useState } from 'react'
import { api, apiUrl } from '../api/client'

/*
  Documents on the table (UI SPEC §6).

  Two things the owner needs and one thing the board needs.

  The owner needs to get a photo out of their pocket and onto the table in one tap, from a
  jobsite, without choosing a category first. And they need everything they have ever put
  there to still be there months later, because the photo of the failed part is often the
  only record of what actually happened that week. Attachments are stored against the
  business, not the item, so removing an item from the table never deletes the evidence.

  The board needs words. PDFs, CSVs and text files are read in the worker and their text
  goes to the board; a photograph and a scanned statement have no text to read at all, and
  most owners photograph their statements. So every row says which of those it is. A file
  that was read and a file that could not be read look identical in a list, and the
  difference decides whether the board is working from your numbers or guessing around
  them.
*/

export interface TableDocument {
  id: string
  kind: string
  kindLabel: string
  fileName: string | null
  contentType: string | null
  sizeBytes: number
  uploadedAt: string
  agendaItemId: string | null
  isImage: boolean
  readable: boolean
  note: string | null
  extractionStatus: 'none' | 'pending' | 'done' | 'scanned' | 'truncated' | 'failed'
  pageCount: number | null
  extractedChars: number
}

/* What the picker offers. Matches the server's allow list; the server is the real gate. */
const ACCEPT = [
  'image/*', '.heic', '.heif',
  'application/pdf', '.pdf',
  '.xls', '.xlsx', '.csv',
  '.doc', '.docx', '.txt', '.rtf', '.pages', '.numbers',
].join(',')

export function Attachments({
  agendaItemId, consultId, compact,
}: {
  agendaItemId?: string
  consultId?: string
  compact?: boolean
}) {
  const [docs, setDocs] = useState<TableDocument[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [pending, setPending] = useState<File | null>(null)
  const [note, setNote] = useState('')
  const picker = useRef<HTMLInputElement>(null)

  const load = async () => {
    // Scoped server-side for a consult so a member asking for the P&L sees only what was
    // handed over in that conversation.
    const all = await api.get<TableDocument[]>(
      consultId ? `/api/table/documents?consultId=${consultId}` : '/api/table/documents')
    setDocs(agendaItemId ? all.filter(d => d.agendaItemId === agendaItemId) : all)
  }

  useEffect(() => { void load().catch(() => setDocs([])) }, [agendaItemId, consultId])

  /*
    Extraction runs in the worker, so a file lands "pending" and finishes a second or two
    later. Polling only while something is actually pending: a dashboard that polls
    forever for nothing is how a quiet product becomes an expensive one.
  */
  useEffect(() => {
    if (!docs?.some(d => d.extractionStatus === 'pending')) return
    const id = setTimeout(() => { void load().catch(() => {}) }, 2500)
    return () => clearTimeout(id)
  }, [docs])

  const upload = async (file: File, withNote: string) => {
    setBusy(true)
    setError(null)
    try {
      const body = new FormData()
      body.append('file', file)
      if (agendaItemId) body.append('agendaItemId', agendaItemId)
      if (consultId) body.append('consultId', consultId)
      if (withNote.trim()) body.append('note', withNote.trim())

      // Not api.post: that serializes JSON and sets a content type, and multipart needs
      // the browser to set its own boundary.
      const response = await fetch(apiUrl('/api/table/documents'), {
        method: 'POST', body, credentials: 'include',
      })

      if (!response.ok) {
        const detail = await response.json().catch(() => null)
        throw new Error(detail?.error ?? 'That did not upload')
      }

      setPending(null)
      setNote('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not upload')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    await api.del(`/api/company/documents/${id}`)
    await load()
  }

  return (
    <div className={`files ${compact ? 'files--compact' : ''}`}>
      <div className="w__title">{consultId ? 'Attached' : 'On the table'}</div>

      {docs === null ? (
        <span className="skeleton" style={{ width: '60%' }} />
      ) : docs.length === 0 ? (
        compact ? null : (
          <p className="dim" style={{ margin: 0 }}>
            Nothing attached yet. A photo of the problem is usually worth more than a
            paragraph describing it.
          </p>
        )
      ) : (
        <ul className="files__list">
          {docs.map(d => (
            <li key={d.id} className="file">
              {d.isImage ? (
                <a href={`/api/documents/${d.id}`} target="_blank" rel="noreferrer">
                  <img className="file__thumb" src={`/api/documents/${d.id}`} alt={d.fileName ?? ''} loading="lazy" />
                </a>
              ) : (
                <span className="file__icon" aria-hidden>{iconFor(d.fileName)}</span>
              )}

              <span className="file__body">
                <a className="file__name" href={`/api/documents/${d.id}`} target="_blank" rel="noreferrer">
                  {d.fileName ?? 'Attachment'}
                </a>
                <span className="file__meta">
                  {size(d.sizeBytes)} · {when(d.uploadedAt)} · {readState(d)}
                </span>
                {d.note && <span className="file__note">{d.note}</span>}
              </span>

              <button className="btn btn--danger" onClick={() => remove(d.id)}>Remove</button>
            </li>
          ))}
        </ul>
      )}

      {error && <div className="signin__error">{error}</div>}

      {pending ? (
        <div className="files__pending">
          <div className="file__name">{pending.name}</div>
          <p className="dim" style={{ margin: '2px 0 var(--space-2)' }}>
            One line on what this shows. Without it the board has a file it cannot open.
          </p>
          <div className="quickadd">
            <input
              className="quickadd__box"
              autoFocus
              value={note}
              placeholder="Matted coat on a senior shih tzu, second visit this month"
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void upload(pending, note) } }}
            />
            <button className="btn btn--brand" disabled={busy} onClick={() => upload(pending, note)}>
              {busy ? 'Adding' : 'Add'}
            </button>
            <button className="btn btn--quiet" disabled={busy} onClick={() => { setPending(null); setNote('') }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`drop ${dragging ? 'is-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault()
            setDragging(false)
            const file = e.dataTransfer.files?.[0]
            if (file) setPending(file)
          }}
          onClick={() => picker.current?.click()}
        >
          <input
            ref={picker}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) setPending(file)
              e.target.value = ''
            }}
          />
          <span>
            {consultId
              ? 'Drop a file here if they asked for one'
              : 'Drop a photo, PDF or spreadsheet here, or click to choose one'}
          </span>
          <span className="dim">Up to 25 MB. Kept on your profile for good.</span>
        </div>
      )}
    </div>
  )
}

function iconFor(fileName: string | null): string {
  const ext = (fileName ?? '').split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'pdf') return 'PDF'
  if (['xls', 'xlsx', 'csv', 'numbers'].includes(ext)) return 'XLS'
  if (['doc', 'docx', 'rtf', 'txt', 'pages'].includes(ext)) return 'DOC'
  return 'FILE'
}

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function when(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/*
  What the board can actually see, said plainly.

  A file that was read and a file that could not be read look identical in a list, and the
  difference decides whether the board is reasoning from your numbers or guessing around
  them. A scan is the common case worth naming: most owners photograph their statements.
*/
function readState(d: TableDocument): string {
  switch (d.extractionStatus) {
    case 'pending':
      return 'reading it now'
    case 'done':
      return d.pageCount
        ? `${d.pageCount} ${d.pageCount === 1 ? 'page' : 'pages'} read`
        : 'read'
    case 'truncated':
      return 'long, first part read'
    case 'scanned':
      return d.note ? 'no text to read' : 'no text to read, add a note'
    case 'failed':
      return 'could not be opened'
    default:
      return d.readable ? 'the board reads your note' : 'the board cannot read this one'
  }
}
